/**
 * Utility functions for handling API errors and extracting user-friendly messages
 */

export interface ApiErrorResponse {
    success?: boolean
    message?: string
    error?: string
    data?: {
        message?: string
        error?: string
    }
    errors?: Record<string, string[]>
}

/**
 * Extracts a user-friendly error message from various error response structures
 * @param error - The error object (typically from axios catch block)
 * @param defaultMessage - Default message to return if no message is found
 * @returns A user-friendly error message
 */
export const extractErrorMessage = (
    error: any,
    defaultMessage: string = 'An error occurred. Please try again.'
): string => {
    // Check if it's an axios error with response
    if (error.response) {
        const data = error.response.data as ApiErrorResponse

        // Try to extract message from various possible locations
        if (data.message) {
            return data.message
        }
        if (data.error) {
            return data.error
        }
        if (data.data?.message) {
            return data.data.message
        }
        if (data.data?.error) {
            return data.data.error
        }

        // If there are field-level errors, combine them
        if (data.errors && typeof data.errors === 'object') {
            const errorMessages = Object.values(data.errors)
                .flat()
                .filter((msg): msg is string => typeof msg === 'string')

            if (errorMessages.length > 0) {
                return errorMessages[0]
            }
        }

        // Use status text as fallback
        if (error.response.statusText) {
            return error.response.statusText
        }
    }

    // If it's a general error with a message
    if (error.message && error.message !== 'Network Error') {
        return error.message
    }

    // Check if error.response exists but couldn't extract message
    if (error.response?.status) {
        if (error.response.status === 404) {
            return 'Resource not found'
        }
        if (error.response.status === 401) {
            return 'Unauthorized. Please log in again.'
        }
        if (error.response.status === 403) {
            return 'You do not have permission to perform this action'
        }
        if (error.response.status >= 500) {
            return 'Server error. Please try again later.'
        }
        if (error.response.status >= 400) {
            return 'Request failed. Please check your input and try again.'
        }
    }

    // Check for network errors
    if (error.code === 'ECONNABORTED') {
        return 'Request timeout. Please try again.'
    }
    if (error.code === 'NETWORK_ERROR' || !window.navigator.onLine) {
        return 'Network error. Please check your internet connection.'
    }

    // Last resort: return default message
    return defaultMessage
}

/**
 * Type guard to check if an error is an axios error
 */
export const isAxiosError = (error: any): boolean => {
    return error && error.response && error.config
}

/**
 * Get HTTP status code from error
 */
export const getErrorStatusCode = (error: any): number | undefined => {
    return error.response?.status
}

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: any): boolean => {
    const status = getErrorStatusCode(error)
    return status === 400 || (error.response?.data?.errors !== undefined)
}

/**
 * Format validation errors into a readable format
 */
export const formatValidationErrors = (error: any): string[] => {
    const data = error.response?.data as ApiErrorResponse

    if (data.errors && typeof data.errors === 'object') {
        return Object.values(data.errors)
            .flat()
            .filter((msg): msg is string => typeof msg === 'string')
    }

    if (data.message) {
        return [data.message]
    }

    return ['Validation failed. Please check your input.']
}